import { Injectable } from '@angular/core';
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {catchError, lastValueFrom, mergeMap, retry, retryWhen, throwError, timeout, timer} from "rxjs";
import {CookieService} from "ngx-cookie-service";
import {Router} from "@angular/router";
import * as CryptoJS from 'crypto-js';
import {environment} from "../environments/environment";

export interface PrimaCallStatus {
  type: 'timeout' | 'retry' | 'success' | 'error';
  attempt?: number;
  data?: any;
}

export class Helper {

  public static getActivityIcon(type: number): string {
    if (type == 1) return 'gavel';
    if (type == 2) return 'poll';
    if (type == 3) return 'question_answer';
    if (type == 4) return 'contact_mail';
    if (type == 5) return 'headset_mic';
    return 'question_mark';
  }
  public static getActivityColor(state: string): string {
    if (state == 'not_started') return 'color:red;';
    if (state == 'in_progress') return 'color:orange;';
    if (state == 'complete') return 'color:green;';
    return 'color:black;';
  }
}

export class AnyResponse {
  data: any;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  authToken: string = '';
  public apiUrl: string = environment.apiUrl;
  private primaUrl: string = '';
  public openAiUrl: string = '';
  public micCheckText: string = '';
  public micCheckThreshold: string = '';
  public silenceDuration: string = '';

  public isLogged: boolean = false;
  public isAdmin: boolean = false;
  public username: string = 'Nepřihlášen';
  public userData: any = {};

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private router: Router
  ) { }

  get headers() {
    return new HttpHeaders({
        'authorization': this.authToken,
        'userAgent': window.navigator.userAgent
    });
  }

  public async getAuthCookie() {
    if (this.cookieService.check('Prima_JWT_Token')) {
      this.authToken = this.cookieService.get('Prima_JWT_Token');
      this.isLogged = true;
      await this.getUserData();
      return true;
    }
    return false;
  }

  public async register(email: string, id: any, data: any) {
    console.log(data);
    let postData = {
      sex: null,
      sex_pronoun: null,
      age: null,
      edu: null,
      size: null,
      nuts3: null,
      clen_dom: null,
      house_income: null,
      name: null,
      address: null,
      borndate: null,
      input: null
    };
    if (data) {
      try {
        postData = JSON.parse(data);
      }
      catch (e) {
        console.log(e);
      }
    }
    try {
      const rD = {
        token: id,
        email: email,
        sex: Number(postData.sex),
        sex_pronoun: Number(postData.sex_pronoun),
        age: Number(postData.age),
        edu: Number(postData.edu),
        size: Number(postData.size),
        nuts3: Number(postData.nuts3),
        clen_dom: Number(postData.clen_dom),
        house_income: Number(postData.house_income),
        name: postData.name,
        address: postData.address,
        borndate: postData.borndate,
        input: Number(postData.input)
      }
      console.log(rD);
      const result = this.http.post<AnyResponse>(
        `${this.apiUrl}/auth/registration`,
        rD
      );
      const response = await lastValueFrom(result);
      console.log(response);
      return false;
    }
    catch (e: any) {
      if (e.error) return e.error.data.message;
      return "Chyba při komunikaci se serverem.";
    }
  }

  public async login(email: string) {
    try {
      // console.log(postData);
      const result = this.http.post<AnyResponse>(
        `${this.apiUrl}/auth/email`,
        {email: email}
      );
      const response = await lastValueFrom(result);
      console.log(response);
      return false;
    }
    catch (e: any) {
      return e.error.data.message;
    }
  }

  public async adminLogin(name: string, pass: string) {
    try {
      // console.log(postData);
      const password = CryptoJS.MD5(pass).toString();
      const result = this.http.post<AnyResponse>(
        `${this.apiUrl}/auth/user/login`,
        {username: name, password: password}
      );
      const response = await lastValueFrom(result);
      console.log(response);
      this.isLogged = true;
      this.isAdmin = true;
      this.userData = response.data.user;
      this.username = response.data.user.username;
      this.authToken = response.data.jwtToken;
      return false;
    }
    catch (e: any) {
      return e.error.data.message;
    }
  }

  public logout() {
    this.isLogged = false;
    this.isAdmin = false;
    this.cookieService.delete('Prima_JWT_Token', '/');
    this.username = 'Nepříhlášen';
    this.userData = {};
    this.authToken = '';
  }

  public async authenticate(token: string) {
    try {
      const result = this.http.post<AnyResponse>(
        `${this.apiUrl}/auth`,
        {
          token: token
        }
      );
      const response = await lastValueFrom(result);
      console.log(response);
      this.authToken = response.data.jwt;
      this.isLogged = true;
      this.cookieService.set('Prima_JWT_Token', this.authToken, 30, '/');
    }
    catch (e) {
      console.error(e);
      this.router.navigate(['invalid_token']);
    }
  }

  public async getUserData() {
    try {
      const result = this.http.get<any>(
        `${this.apiUrl}/respondent/data`,
        {headers: this.headers}
      );
      const response = await lastValueFrom(result);
      console.log(response);
      this.username = response.data.email;
      this.userData = response.data;
      await this.getAppSettings();
    }
    catch (e) {
      console.error(e);
      this.logout();
      this.router.navigate(['invalid_token']);
    }
  }

  private async getAppSettings() {
    try {
      const result = this.http.get<any>(
        `${this.apiUrl}/auth/appsettings`,
        {headers: this.headers}
      );
      const response = await lastValueFrom(result);
      console.log(response);
      this.primaUrl = response.data.primaUrl;
      this.openAiUrl = response.data.openAiUrl;
      this.micCheckText = response.data.micCheckText;
      this.micCheckThreshold = response.data.micCheckThreshold;
      this.silenceDuration = response.data.silenceDuration;
    }
    catch (e) {
      console.error(e);
      this.router.navigate(['invalid_token']);
    }
  }

  public async getAvailableActivities() {
    try {
      const result = this.http.get<any>(
        `${this.apiUrl}/respondent/activities`,
        {headers: this.headers}
      );
      const response = await lastValueFrom(result);
      console.log(response);
      return response.data;
    }
    catch (e) {
      console.error(e);
      return {};
    }
  }

  public async getActivityDetail(id: any) {
    try {
      const result = this.http.get<any>(
        `${this.apiUrl}/respondent/activity/${id}`,
        {headers: this.headers}
      );
      const response = await lastValueFrom(result);
      console.log(response);
      return response.data;
    } catch (e) {
      console.error(e);
      this.router.navigate(['not-found']);
    }
  }

  public async setActivityState(aid: any, state: string) {
    console.log('Set activity state',aid,state);
    const result = this.http.patch<any>(
      `${this.apiUrl}/respondent/activity/${aid}/${state}`,
      [],
      {headers: this.headers}
    );
    const response = await lastValueFrom(result);
    console.log(response);
    return response.data;
  }

  public async anonymizeRequest() {
    const result = this.http.patch<any>(
      `${this.apiUrl}/respondent/anonymize-request`,
      [],
      {headers: this.headers}
    );
    const response = await lastValueFrom(result);
    console.log(response);
    return response.data;
  }

  public async getRespondents() {
    const result = this.http.get<any>(
      `${this.apiUrl}/data/overview/all`,
      {headers: this.headers}
    );
    const response = await lastValueFrom(result);
    console.log(response);
    let data: any = [];
    response.data.forEach((r: { respondentData: { respondent_id: any; email: any; }; activities: any; }) => {
      data.push({
        id: r.respondentData.respondent_id,
        email: r.respondentData.email,
        activities: r.activities
      });
    });
    return data;
  }

  public async getResponendt(id: any) {
    const result = this.http.get<any>(
      `${this.apiUrl}/data/overview/${id}`,
      {headers: this.headers}
    );
    const response = await lastValueFrom(result);
    console.log(response);
    return response.data;
  }

  public async getOpenAiToken() {
    const result = this.http.post<any>(
      `${this.apiUrl}/auth/openai`,
      [],
      {headers: this.headers}
    );
    const response = await lastValueFrom(result);
    console.log(response);
    return response.data.data.client_secret.value;
  }

  public async getPrimaToken() {
    const result = this.http.post<any>(
      `${this.apiUrl}/auth/prima`,
      [],
      {headers: this.headers}
    );
    const response = await lastValueFrom(result);
    console.log(response);
    return response.data.data.access_token;
  }

  public async primaCall(token: string, data: any, onStatus?: (status: PrimaCallStatus) => void) {
    let attempt = 0;
    const result$ = this.http.post<any>(
      `${this.primaUrl}/process`,
      JSON.stringify(data),
      {
        headers: new HttpHeaders({
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'text/plain'
        })
      }
    ).pipe(
      timeout(60000),
      retry({
        count: 2,
        delay: (error, retryIndex) => {
          const attemptNumber = retryIndex + 1;

          // Only retry on timeout
          if (error?.name === 'TimeoutError') {
            attempt = attemptNumber;
            onStatus?.({ type: 'timeout', attempt: attemptNumber });
            onStatus?.({ type: 'retry', attempt: attemptNumber });
            return timer(1000);
          }
          return throwError(() => error);
        }
      }),
      catchError(err => {
        onStatus?.({ type: 'error', attempt });
        return throwError(() =>
          err?.name === 'TimeoutError'
            ? new Error('Prima API did not respond after 3 retries')
            : err
        );
      })
    );
    const response = await lastValueFrom(result$);
    onStatus?.({ type: 'success', attempt, data: response });
    return response;
  }

  public async uploadRecording(blob: Blob, qid: string, fid: string) {
    const respid = this.userData.respondent_id;
    const file = new File([blob], respid+"_"+qid+"_"+fid+".webm");
    const formData = new FormData();
    formData.append('audio', file);
    formData.append('questionId', qid);
    formData.append('followupId', fid);
    const result = this.http.post<any>(
      `${this.apiUrl}/respondent/upload`,
      formData,
      {headers: this.headers}
    );
    const response = await lastValueFrom(result);
    console.log(response);
  }

  public async uploadAnswer(text: string, qid: string, fid: string) {
    const result = this.http.post<any>(
      `${this.apiUrl}/respondent/answer`,
      {
        "text": text,
        "questionId": qid,
        "followupId": fid
      },
      {headers: this.headers}
    );
    const response = await lastValueFrom(result);
    console.log(response);
  }

  public async changeGender(gender: number) {
    const result = this.http.patch<any>(
      `${this.apiUrl}/respondent/data`,
      {
        "sex_pronoun": gender
      },
      {headers: this.headers}
    );
    this.userData.sex_pronoun = gender;
    const response = await lastValueFrom(result);
    console.log(response);
    return response.data;
  }
  public async saveTermsData(name: any, address: any, borndate: any) {
    const result = this.http.patch<any>(
      `${this.apiUrl}/respondent/data`,
      {
        "name": name,
        "address": address,
        "borndate": borndate
      },
      {headers: this.headers}
    );
    const response = await lastValueFrom(result);
    console.log(response);
    return response.data;
  }

  public async getAnonymizations() {
    const result = this.http.get<any>(
      `${this.apiUrl}/respondent/anonymizations`,
      {headers: this.headers}
    );
    const response = await lastValueFrom(result);
    console.log(response);
    return response.data;
  }

  public async confirmAnonymization(id: any) {
    const result = this.http.patch<any>(
      `${this.apiUrl}/respondent/anonymize/${id}`,
      [],
      {headers: this.headers}
    );
    const response = await lastValueFrom(result);
    console.log(response);
    return response;
  }

  public async getJobToken() {
    const result = this.http.get<any>(
      `${this.apiUrl}/job/token`,
      {headers: this.headers}
    );
    const response = await lastValueFrom(result);
    console.log(response);
    return response.data.token;
  }

  public async quickAnonymize(id: any) {
    const result = this.http.patch<any>(
      `${this.apiUrl}/respondent/quick-anonymize/${id}`,
      [],
      {headers: this.headers}
    );
    const response = await lastValueFrom(result);
    console.log(response);
    return response;
  }

  public async getCanUseMic() {
    const result = this.http.get<any>(
      `${this.apiUrl}/respondent/miccheck`,
      {headers: this.headers}
    );
    const response = await lastValueFrom(result);
    console.log(response);
    return response.data;
  }

  public async changeInput(prefersText: boolean) {
    let input = 0;
    if (prefersText) input = 1;
    const result = this.http.patch<any>(
      `${this.apiUrl}/respondent/data`,
      {
        "input": input
      },
      {headers: this.headers}
    );
    this.userData.input = input;
    const response = await lastValueFrom(result);
    console.log(response);
    return response.data;
  }

  public async getHelp() {
    try {
      const result = this.http.get<any>(
        `${this.apiUrl}/respondent/help`,
        {headers: this.headers}
      );
      const response = await lastValueFrom(result);
      console.log('***HELP:');
      console.log(response);
      return response.data;
    }
    catch (e) {
      console.error(e);
      return {};
    }
  }


}
